import { BuildRCAttribute } from "@/common/function/RCAttribute";
import {
  Button,
  Input,
  Listbox,
  Popover,
  PopoverSurface,
  PositioningImperativeRef,
  Radio,
  RadioGroup,
  RadioGroupOnChangeData,
  RadioProps,
  useRestoreFocusTarget,
} from "@fluentui/react-components";
import { cloneDeep, isEmpty } from "lodash";
import React, { useEffect, useId, useRef, useState } from "react";
import {
  ExpandClose,
  ExpandCloseSmall,
  ExpandOpen,
  ExpandOpenSmall,
} from "../Icons/IconsCustom";
import Nodata from "../Nodata";
import {
  EAlignment,
  EPosition,
  ITreeInfo,
  ITreeSelectProps,
  SMALL,
  TreeInfo,
} from "./Models";
import {
  ButtonApplyWrapper,
  PopoverWrapper,
  TreeSelectBaseWrapper,
} from "./Style";
import TreeCheckboxButton from "./TreeCheckBoxButton";
import { CategoryFilterOperatorEnum } from "@/common/enums";
import { useTranslation } from "react-i18next";

let mappingValueToListChild: Record<string, string[]> = {};
let mappingValueTemp: Record<string, string> = {};

const TreeSelect = React.forwardRef((props: ITreeSelectProps, ref) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const headerId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLInputElement>(null);
  const positioningRef = useRef<PositioningImperativeRef>(null);
  const restoreFocusTargetAttribute = useRestoreFocusTarget();
  const [optsFilter, setOptsFilter] = useState<TreeInfo[]>([]);
  const [mappingValue, setMappingValue] = useState<Record<string, string>>({});
  const [showValue, setShowValue] = useState<string[]>([]);
  const [showIconCloseValue, setShowIconCloseValue] = useState<boolean>(false);
  const [mappingValueToListChildState, setMappingValueToListChild] = useState<
    Record<string, string[]>
  >({});

  useEffect(() => {
    if (wrapperRef?.current && positioningRef.current) {
      positioningRef.current?.setTarget(wrapperRef.current);
    }
  }, [wrapperRef, positioningRef]);

  useEffect(() => {
    if (props.items.length) {
      mappingValueToListChild = {};
      const items: TreeInfo[] = (props.items || []).map((item: ITreeInfo) => {
        return new TreeInfo({
          ...item,
          isCollapsed: (props.expanded || []).includes(item?.guid),
        });
      });
      const locations: TreeInfo[] = items.map((item: TreeInfo) => {
        item.setParentIds(undefined, props.showExpandAll);
        item.setChildDeep();
        return item;
      });
      const idsParentExpand: string[] = findNodeTreeIdsParent(
        locations,
        props.expanded || [],
        []
      );
      const locationExpand: TreeInfo[] = locations.map((item: TreeInfo) => {
        item.setExpand(idsParentExpand);
        return item;
      });
      setOptsFilter(cloneDeep(locationExpand));
      setValueForMappingValue(locationExpand);
      convertMappingValueToArr(locationExpand, {});
      setMappingValueToListChild(mappingValueToListChild);
    }
    return () => {
      mappingValueTemp = {};
      mappingValueToListChild = {};
    };
  }, [JSON.stringify(props.items), JSON.stringify(props.expanded)]);

  useEffect(() => {
    if (isEmpty(mappingValueToListChild) && props.items.length) {
      mappingValueToListChild = {};
      convertMappingValueToArr(optsFilter, {});
      setMappingValueToListChild(mappingValueToListChild);
    }
    if (props.items.length) {
      setValueDisplayLabel();
    }
  }, [JSON.stringify(props.items), JSON.stringify(props.values)]);

  let temp: string[] = [];

  const findNodeTreeIdsParent = (
    nodes: TreeInfo[],
    listId: string[],
    idsParents: string[]
  ): string[] => {
    if (nodes?.length && listId?.length) {
      nodes.forEach((item: TreeInfo) => {
        if (listId.includes(item.guid) && item.parentIds.length) {
          temp = [...idsParents, ...item.parentIds, item.guid];
        }
        if (item.children.length) {
          findNodeTreeIdsParent(item.children, listId, [...new Set(temp)]);
        }
      });
      return [...new Set(temp)];
    }
    return [];
  };

  const setValueDisplayLabel = (): void => {
    let index = 0;
    let items: string[] = props.values ? cloneDeep(props.values) : [];
    items.sort((a: string, b: string) => {
      if (Number(a) < Number(b)) return -1;
      if (Number(a) > Number(b)) return 1;
      return 0;
    });
    while (index < items.length && !isEmpty(mappingValueToListChild)) {
      const listChild: string[] = mappingValueToListChild[items[index]] || [];
      items = items.filter((id: string) => !listChild.includes(id));
      index += 1;
    }
    setShowValue([...items]);
  };

  const setValueForMappingValue = (items: TreeInfo[]) => {
    const data = convertMappingValue(items, {});
    setMappingValue({ ...data });
  };

  const convertMappingValue = (
    items: TreeInfo[],
    objMap: Record<string, string>
  ): Record<string, string> => {
    const temp: Record<string, string> = objMap ? { ...objMap } : {};
    if (items.length) {
      (items as TreeInfo[]).forEach((item: TreeInfo) => {
        temp[item.guid] = item.name || "";
        if (item.children?.length) {
          convertMappingValue(item.children, temp);
        }
      });
    }
    mappingValueTemp = { ...mappingValueTemp, ...temp };
    return mappingValueTemp;
  };

  const convertMappingValueToArr = (
    items: TreeInfo[],
    objMap: Record<string, string[]>
  ): void => {
    if (items.length) {
      const temp: Record<string, string[]> = objMap ? { ...objMap } : {};
      if (items.length) {
        (items as TreeInfo[]).forEach((item: TreeInfo) => {
          temp[item.guid] = [...item.childDeep];
          if (item.children?.length) {
            convertMappingValueToArr(item.children, temp);
          }
        });
      }
      mappingValueToListChild = { ...mappingValueToListChild, ...temp };
    }
  };

  const getWithElement = (): number => {
    const el = document.getElementById(
      `select_context_${props.rcName || "input"}`
    );
    return el?.offsetWidth ? el.offsetWidth : 0;
  };

  const onChangeRadio = (
    _ev: React.FormEvent<HTMLDivElement>,
    data: RadioGroupOnChangeData
  ): void => {
    if (data?.value && props.onCheckCondition) {
      props.onCheckCondition(data.value);
    }
  };

  const renderRadioButton = (): JSX.Element => {
    const options: RadioProps[] = [
      {
        value: "and",
        label: CategoryFilterOperatorEnum.And,
      },
      {
        value: "or",
        label: CategoryFilterOperatorEnum.Or,
      },
    ];

    return (
      <div className={`blk-choice-group ${props.size ? "size-small" : ""}`}>
        <RadioGroup
          layout="horizontal"
          onChange={onChangeRadio}
          value={props.condition}
          {...BuildRCAttribute(`tree_select.radio_group.${props.rcName}`)}
        >
          {options.map((option: RadioProps, index: number) => (
            <Radio
              key={index}
              value={option.value}
              label={t(`${option.label}`)}
              {...BuildRCAttribute(`tree_select.radio.${props.rcName}`)}
            />
          ))}
        </RadioGroup>
      </div>
    );
  };

  const renderButtonApplyAndClear = (): JSX.Element => {
    return (
      <ButtonApplyWrapper className="ButtonApplyWrapper" $theme={props.theme}>
        <Button
          onClick={() => props.onClear && props.onClear()}
          size="small"
          {...BuildRCAttribute(`tree_select.btn_clear.${props.rcName}`)}
        >
          {t("Clear")}
        </Button>
        <div
          className="btn-apply"
          onClick={() => props.onApply && props.onApply()}
          tabIndex={3}
          {...BuildRCAttribute(`tree_select.btn_apply.${props.rcName}`)}
        >
          {t("Apply")}
        </div>
      </ButtonApplyWrapper>
    );
  };

  const displayValue = (values: string[]): string => {
    if (props.displayLabelValue && props.values?.length) {
      return props.displayLabelValue;
    }
    const listMapLabel: string[] = (values || []).map((value: string) => {
      return mappingValue[value];
    });
    return listMapLabel.filter((name: string) => !!name).join(", ");
  };

  const renderIconLeft = (): JSX.Element | null => {
    if (
      props.values?.length &&
      !!displayValue(props.values) &&
      props.iconLeftActive
    ) {
      return props.iconLeftActive;
    }
    return props.iconLeft || null;
  };

  const onToggleShowFunc = function (): void {
    setOpen(!open);
    if (props.onToggleShow) {
      props.onToggleShow(!open);
    }
  };

  const onChangeToggleContextMenu = (isOpen: boolean) => {
    setOpen(isOpen);
  };

  React.useImperativeHandle(ref, () => ({
    onChangeToggleContextMenu,
  }));

  const renderOnlyTree = (): JSX.Element => {
    return optsFilter?.length ? (
      <TreeSelectBaseWrapper className="TreeSelectBaseWrapper">
        <Listbox style={props.style}>
          {optsFilter.map((item: TreeInfo) => (
            <TreeCheckboxButton
              key={`tree-select-${item.guid}`}
              node={item}
              values={props.values || []}
              onChange={props.onChange}
              rcName={props.rcName}
              mappingValueToListChild={mappingValueToListChild}
              actionFavorites={props.actionFavorites}
              isSingle={props.isSingle}
              expanded={props.expanded}
              isNotCheckbox={props.isNotCheckbox}
              isOnlySelectOneValue={props.isOnlySelectOneValue}
              blockNodeRightClick={props.blockNodeRightClick}
            />
          ))}
        </Listbox>
      </TreeSelectBaseWrapper>
    ) : (
      <Nodata />
    );
  };

  const onRenderIconDropdown = (): JSX.Element => {
    return open ? (
      props.size === SMALL ? (
        <ExpandOpenSmall
          onClick={() => (!props.disabled ? onToggleShowFunc() : undefined)}
        />
      ) : (
        <ExpandOpen
          onClick={() => (!props.disabled ? onToggleShowFunc() : undefined)}
        />
      )
    ) : props.size === SMALL ? (
      <ExpandCloseSmall
        onClick={
          !props.disabled
            ? () => {
                onToggleShowFunc();
                inputRef?.current?.focus();
              }
            : undefined
        }
      />
    ) : (
      <ExpandClose
        onClick={
          !props.disabled
            ? () => {
                onToggleShowFunc();
                inputRef?.current?.focus();
              }
            : undefined
        }
      />
    );
  };

  const setValueShowIconClose = (isShowIconClose: boolean): void => {
    setShowIconCloseValue(isShowIconClose);
  };

  const renderSelect = (): JSX.Element => {
    return (
      <TreeSelectBaseWrapper
        ref={wrapperRef}
        className="TreeSelectBaseWrapper"
        id={`select_context_${props.rcName || "input"}`}
        $width={getWithElement() || 300}
        $theme={props.theme}
        $isSmall={props.size === SMALL}
      >
        <Input
          {...restoreFocusTargetAttribute}
          className={`input-locations-sl ${
            props.size === SMALL ? "size-small" : ""
          } ${props.values?.length ? "active-location-tree" : ""}`}
          onClick={() => setOpen((s) => !s)}
          ref={inputRef}
          style={{ width: "100%" }}
          value={props.values ? displayValue(showValue) : ""}
          placeholder={props?.lblSelect}
          contentBefore={renderIconLeft()}
          contentAfter={
            <div
              onMouseEnter={() =>
                props.showIconClear && props.isNotCheckbox && displayValue(showValue)
                  ? setValueShowIconClose(true)
                  : undefined
              }
              onMouseOut={() =>
                props.showIconClear && props.isNotCheckbox && displayValue(showValue)
                  ? setValueShowIconClose(false)
                  : undefined
              }
              className={
                props.disabled ? "input-icon-drd-disable" : "input-icon-drd"
              }
            >
              {props.isNotCheckbox &&
              showIconCloseValue &&
              displayValue(showValue) ? (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  onClick={() => props.onChange && props.onChange([])}
                >
                  <path
                    d="M2.08859 2.21569L2.14645 2.14645C2.32001 1.97288 2.58944 1.9536 2.78431 2.08859L2.85355 2.14645L6 5.293L9.14645 2.14645C9.34171 1.95118 9.65829 1.95118 9.85355 2.14645C10.0488 2.34171 10.0488 2.65829 9.85355 2.85355L6.707 6L9.85355 9.14645C10.0271 9.32001 10.0464 9.58944 9.91141 9.78431L9.85355 9.85355C9.67999 10.0271 9.41056 10.0464 9.21569 9.91141L9.14645 9.85355L6 6.707L2.85355 9.85355C2.65829 10.0488 2.34171 10.0488 2.14645 9.85355C1.95118 9.65829 1.95118 9.34171 2.14645 9.14645L5.293 6L2.14645 2.85355C1.97288 2.67999 1.9536 2.41056 2.08859 2.21569L2.14645 2.14645L2.08859 2.21569Z"
                    fill="#212121"
                  />
                </svg>
              ) : (
                onRenderIconDropdown()
              )}
            </div>
          }
          {...BuildRCAttribute(`input_tree_select.${props.rcName}.${open}`)}
        />
        <Popover
          onOpenChange={(_, data) => setOpen(data.open)}
          trapFocus
          open={open}
          positioning={{
            positioningRef,
            position: EPosition.BELOW,
            align: props.align || EAlignment.START,
          }}
          mountNode={undefined}
        >
          <PopoverSurface
            aria-labelledby={headerId}
            className="width_custom_popover"
            style={{
              maxHeight: "300px",
              overflow: "auto",
              minWidth: getWithElement() - 12,
              padding: "4px",
            }}
          >
            <React.Fragment>
              {optsFilter?.length ? (
                <PopoverWrapper
                  className="PopoverWrapper"
                  $isSmall={props.size === SMALL}
                >
                  {props.showRadio ? renderRadioButton() : null}
                  <Listbox
                    className={`list-box-locations pd-list ${
                      props.onApply || props.onClear ? "box-shadow-none" : ""
                    }`}
                    {...BuildRCAttribute(
                      `input_tree_select_list.${props.rcName}`
                    )}
                  >
                    {optsFilter.map((item: TreeInfo) => (
                      <TreeCheckboxButton
                        key={`tree-select-${item.guid}`}
                        node={item}
                        values={props.values || []}
                        onChange={props.onChange}
                        rcName={props.rcName}
                        mappingValueToListChild={mappingValueToListChildState}
                        actionFavorites={props.actionFavorites}
                        isSingle={props.isSingle}
                        expanded={props.expanded}
                        isNormalLogic={props.isNormalLogic}
                        isNotCheckbox={props.isNotCheckbox}
                        size={props.size}
                        isOnlySelectOneValue={props.isOnlySelectOneValue}
                        blockNodeRightClick={props.blockNodeRightClick}
                      />
                    ))}
                  </Listbox>
                  {props.onApply || props.onClear
                    ? renderButtonApplyAndClear()
                    : null}
                </PopoverWrapper>
              ) : (
                <Nodata />
              )}
            </React.Fragment>
          </PopoverSurface>
        </Popover>
      </TreeSelectBaseWrapper>
    );
  };

  const renderSelectTree = (): JSX.Element => {
    if (props.isDisplayOnlyTree) {
      return renderOnlyTree();
    }
    return renderSelect();
  };

  return <>{renderSelectTree()}</>;
});

export default TreeSelect;
